/**
 * The feng shui landing's in-canvas example: the ad's rearrangement played
 * on the real canvas instead of described in a sheet.
 *
 * One small external store drives what the canvas overlays (red issue
 * callouts and the coffin line on the "before" room, green fix callouts
 * and the qi path as pieces land, the live score) and one orchestrator,
 * {@link playFengShuiExample}, moves the pieces frame by frame along the
 * same beats as the Reel ({@link EXAMPLE_SEQUENCE}). When the sequence
 * lands it applies the saved example for real (`applyFengShuiSuggestion`
 * with `example: true`), which stamps the rationale, appends the
 * consultation and fires `feng_shui_example_applied` — the Meta
 * optimization event.
 *
 * The whole thing runs inside one history gesture, so Undo takes the room
 * back to the coffin position in a single step.
 */

import { useSyncExternalStore } from "react";
import {
  EXAMPLE_CHI_POINTS,
  EXAMPLE_COFFIN_LINE,
  EXAMPLE_FIXES,
  EXAMPLE_ISSUES,
  EXAMPLE_PIECES,
  EXAMPLE_SCORE,
  EXAMPLE_SEQUENCE,
  EXAMPLE_TAG_AT,
  buildExampleSuggestion,
  examplePieceId,
  isFengShuiStarterRoom,
  type ExampleFixKey,
  type ExampleIssueKey,
} from "./fengShuiExample";
import { useDesign } from "./store";
import { addBreadcrumb, track } from "./telemetry";

export type FengShuiDemoPhase =
  /** Nothing on the canvas. */
  | "idle"
  /** The "before" room: issues called out, score 38, waiting for the tap. */
  | "before"
  /** "Consulting the bagua…" — the beat between the tap and the first move. */
  | "thinking"
  /** Pieces are moving and fixes are landing. */
  | "moving"
  /** Landed: everything in place, score 94, the bed's "why" on screen. */
  | "after";

export interface FengShuiDemoCallout {
  key: string;
  text: string;
  tone: "issue" | "fix";
  /** Room inches. */
  at: { x: number; y: number };
}

export interface FengShuiDemoState {
  phase: FengShuiDemoPhase;
  score: number;
  callouts: FengShuiDemoCallout[];
  /** Red arrow from the door to the foot of the bed. */
  coffinLine: boolean;
  /** Teal dotted path of qi flowing in from the door. */
  chi: boolean;
  /** Piece whose "why" the landing is pointing at (the bed, once landed). */
  focusPieceId: string | null;
  /** Consultation id of the applied example, once landed. */
  consultationId: string | null;
}

const IDLE: FengShuiDemoState = {
  phase: "idle",
  score: EXAMPLE_SCORE.before,
  callouts: [],
  coffinLine: false,
  chi: false,
  focusPieceId: null,
  consultationId: null,
};

let state: FengShuiDemoState = IDLE;
const listeners = new Set<() => void>();

function set(patch: Partial<FengShuiDemoState>): void {
  state = { ...state, ...patch };
  for (const l of listeners) l();
}

export function getFengShuiDemo(): FengShuiDemoState {
  return state;
}

export function subscribeFengShuiDemo(listener: () => void): () => void {
  listeners.add(listener);
  return () => {
    listeners.delete(listener);
  };
}

export function useFengShuiDemo(): FengShuiDemoState {
  return useSyncExternalStore(
    subscribeFengShuiDemo,
    getFengShuiDemo,
    getFengShuiDemo
  );
}

const issueCallout = (key: ExampleIssueKey): FengShuiDemoCallout => ({
  key: `issue:${key}`,
  text: EXAMPLE_ISSUES[key],
  tone: "issue",
  at: EXAMPLE_TAG_AT[key],
});

const fixCallout = (key: ExampleFixKey): FengShuiDemoCallout => ({
  key: `fix:${key}`,
  text: EXAMPLE_FIXES[key],
  tone: "fix",
  at: EXAMPLE_TAG_AT[key === "desk" ? "deskFix" : key],
});

/** Put the "before" overlays on the canvas (the landing's first frame). */
export function showFengShuiBefore(): void {
  if (state.phase !== "idle" && state.phase !== "before") return;
  if (!isFengShuiStarterRoom(useDesign.getState().design)) return;
  set({
    ...IDLE,
    phase: "before",
    coffinLine: true,
    callouts: (["coffin", "desk", "dresser"] as const).map(issueCallout),
  });
}

/**
 * Put the landed state back on the canvas without replaying (a reload
 * after the example was applied but before the visitor moved on).
 */
export function showFengShuiAfter(consultationId: string | null): void {
  if (state.phase === "thinking" || state.phase === "moving") return;
  if (!isFengShuiStarterRoom(useDesign.getState().design)) return;
  set({
    ...IDLE,
    phase: "after",
    score: EXAMPLE_SCORE.after,
    chi: true,
    callouts: (Object.keys(EXAMPLE_FIXES) as ExampleFixKey[]).map(fixCallout),
    focusPieceId: examplePieceId("bed"),
    consultationId,
  });
}

/** Take every overlay off the canvas. */
export function clearFengShuiDemo(): void {
  if (state.phase === "thinking" || state.phase === "moving") cancelled = true;
  state = IDLE;
  for (const l of listeners) l();
}

let cancelled = false;
let playing: Promise<string | null> | null = null;

const clamp01 = (t: number) => Math.max(0, Math.min(1, t));
const easeInOutCubic = (t: number) =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
const normalizeDeg = (deg: number) => ((deg % 360) + 360) % 360;
/** Signed shortest turn from `a` to `b`, in degrees. */
const shortestTurn = (a: number, b: number) => {
  const d = normalizeDeg(b - a);
  return d > 180 ? d - 360 : d;
};

function nextFrame(cb: (now: number) => void): void {
  if (typeof requestAnimationFrame === "function") requestAnimationFrame(cb);
  else setTimeout(() => cb(Date.now()), 16);
}

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function")
    return false;
  try {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  } catch {
    return false;
  }
}

/**
 * Play the rearrangement on the canvas, then apply the example for real.
 * Resolves with the consultation id, or `null` when the room wasn't the
 * starter room any more or the sequence was cancelled. Calling it while a
 * run is in progress returns the in-flight promise.
 */
export function playFengShuiExample(
  opts: { reduceMotion?: boolean } = {}
): Promise<string | null> {
  if (playing) return playing;
  playing = run(opts).finally(() => {
    playing = null;
  });
  return playing;
}

async function run({
  reduceMotion = prefersReducedMotion(),
}: {
  reduceMotion?: boolean;
}): Promise<string | null> {
  const store = useDesign.getState();
  if (!isFengShuiStarterRoom(store.design)) return null;
  cancelled = false;

  const suggestion = buildExampleSuggestion(store.design);
  const targets = new Map(suggestion.updates.map((u) => [u.id, u] as const));
  const starts = new Map(
    store.design.furniture
      .filter((f) => targets.has(f.id))
      .map((f) => [f.id, { x: f.x, y: f.y, rotation: f.rotation }] as const)
  );
  const moves = EXAMPLE_PIECES.filter((p) =>
    targets.has(examplePieceId(p.key))
  ).map((p) => {
    const id = examplePieceId(p.key);
    const from = starts.get(id)!;
    const to = targets.get(id)!;
    return {
      id,
      at: EXAMPLE_SEQUENCE.moveAtMs[p.key],
      from,
      to,
      turn: shortestTurn(from.rotation, to.rotation),
    };
  });
  const fixes = (Object.keys(EXAMPLE_SEQUENCE.fixAtMs) as ExampleFixKey[])
    .map((key) => ({ key, at: EXAMPLE_SEQUENCE.fixAtMs[key] }))
    .sort((a, b) => a.at - b.at);
  const firstFixAt = fixes[0]?.at ?? 0;

  const poseAt = (elapsed: number) =>
    moves.map((m) => {
      const t = easeInOutCubic(
        clamp01((elapsed - m.at) / EXAMPLE_SEQUENCE.moveMs)
      );
      return {
        id: m.id,
        x: m.from.x + (m.to.x - m.from.x) * t,
        y: m.from.y + (m.to.y - m.from.y) * t,
        rotation: normalizeDeg(m.from.rotation + m.turn * t),
      };
    });
  const scoreAt = (elapsed: number) => {
    const t = clamp01(
      (elapsed - firstFixAt) /
        Math.max(1, EXAMPLE_SEQUENCE.scoreDoneMs - firstFixAt)
    );
    return Math.round(
      EXAMPLE_SCORE.before + (EXAMPLE_SCORE.after - EXAMPLE_SCORE.before) * t
    );
  };

  // One undo entry for the whole sequence (think → moves → apply).
  store.beginGesture();
  const stillStarter = () =>
    !cancelled && isFengShuiStarterRoom(useDesign.getState().design);
  const bail = () => {
    useDesign.getState().cancelGesture();
    clearFengShuiDemo();
    return null;
  };
  try {
    set({ phase: "thinking", coffinLine: true });
    addBreadcrumb("feng-shui-demo:play", { reduceMotion });

    if (!reduceMotion) {
      await wait(EXAMPLE_SEQUENCE.thinkMs);
      if (!stillStarter()) return bail();
      // Issues clear as the room starts to move.
      set({ phase: "moving", coffinLine: false, callouts: [] });
      await new Promise<void>((resolve) => {
        const t0 =
          typeof performance !== "undefined" ? performance.now() : Date.now();
        const shown = new Set<ExampleFixKey>();
        const tick = (nowRaw: number) => {
          if (!stillStarter()) return resolve();
          const now = Number.isFinite(nowRaw) ? nowRaw : Date.now();
          const elapsed = now - t0;
          useDesign.getState().setFurniturePoses(poseAt(elapsed));
          const landed = fixes.filter(
            (f) => elapsed >= f.at && !shown.has(f.key)
          );
          const patch: Partial<FengShuiDemoState> = { score: scoreAt(elapsed) };
          if (landed.length > 0) {
            for (const f of landed) shown.add(f.key);
            patch.callouts = [
              ...state.callouts,
              ...landed.map((f) => fixCallout(f.key)),
            ];
            if (landed.some((f) => f.key === "chi")) patch.chi = true;
          }
          set(patch);
          if (elapsed < EXAMPLE_SEQUENCE.scoreDoneMs) nextFrame(tick);
          else resolve();
        };
        nextFrame(tick);
      });
      if (!stillStarter()) return bail();
    }

    // Land exactly on the authored poses, then make it real: rationale on
    // every piece, a consultation in history, and the funnel event.
    useDesign.getState().setFurniturePoses(poseAt(Number.POSITIVE_INFINITY));
    const consultationId = useDesign.getState().applyFengShuiSuggestion({
      updates: suggestion.updates,
      additions: [],
      removals: [],
      consultation: { mode: "rearrange", overall: suggestion.overall },
      example: true,
    });
    useDesign.getState().endGesture();
    set({
      phase: "after",
      score: EXAMPLE_SCORE.after,
      coffinLine: false,
      chi: true,
      callouts: fixes.map((f) => fixCallout(f.key)),
      focusPieceId: examplePieceId("bed"),
      consultationId,
    });
    track("feng_shui_example_animation_completed", { reduced_motion: reduceMotion });
    return consultationId;
  } catch (err) {
    addBreadcrumb("feng-shui-demo:failed", { error: String(err) }, "error");
    return bail();
  }
}

/** Geometry the canvas layer draws; re-exported so the layer has one import. */
export const FENG_SHUI_DEMO_GEOMETRY = {
  coffinLine: EXAMPLE_COFFIN_LINE,
  chiPoints: EXAMPLE_CHI_POINTS,
} as const;

/** Test-only. */
export function _resetFengShuiDemoForTests(): void {
  cancelled = true;
  playing = null;
  state = IDLE;
  listeners.clear();
}
